import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { PlusIcon } from '../../../components/icons';
import { ContainedButton } from '../../../components/buttons';

import { Style } from '../../styles/types';
import { DropdownOption } from '../../labels/components/settings/DropdownOption';
import { useStyles } from '../../styles/routes/hooks';

interface PartnerStylesMultiSelectProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  partnerId: string;
  partnerName: string;
  selectedStyleIds: string[];
  onStylesChange: (styleIds: string[]) => void;
  error?: boolean;
}

export function PartnerStylesMultiSelect({
  isOpen,
  setIsOpen,
  partnerId,
  partnerName,
  selectedStyleIds,
  onStylesChange,
  error
}: PartnerStylesMultiSelectProps) {
  // Fetch only partner-specific styles
  const { styles: fetchedStyles, isLoading } = useStyles({
    filterType: 'partner',
    partnerId
  });
  const [localSelectedIds, setLocalSelectedIds] =
    useState<string[]>(selectedStyleIds);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalSelectedIds(selectedStyleIds);
  }, [selectedStyleIds]);

  // Create style options with "All" option (only if there are partner styles)
  const styles = useMemo(() => {
    if (fetchedStyles.length === 0) return [];
    return [
      {
        style_id: 'all-partner',
        system_name: `All ${partnerName} Styles`,
        display_name: `All ${partnerName} Styles`
      } as Style,
      ...fetchedStyles
    ];
  }, [fetchedStyles, partnerName]);

  const styleNames = useMemo(
    () =>
      new Map(styles.map(s => [s.style_id, s.display_name || s.system_name])),
    [styles]
  );

  // Check if "all-partner" is selected
  const isAllSelected = localSelectedIds.includes('all-partner');

  // Get display text for button
  const getDisplayText = (): string => {
    if (fetchedStyles.length === 0) {
      return 'No Partner Styles';
    }
    if (isAllSelected) {
      return `All ${partnerName} Styles`;
    }
    if (localSelectedIds.length === 0) {
      return 'Select Partner Styles';
    }
    return `${localSelectedIds.length} Partner Style${
      localSelectedIds.length !== 1 ? 's' : ''
    }`;
  };

  const handleToggleStyle = (styleId: string) => {
    let newSelectedIds: string[];

    if (styleId === 'all-partner') {
      // Toggle "all" selection
      if (isAllSelected) {
        newSelectedIds = [];
      } else {
        newSelectedIds = ['all-partner'];
      }
    } else {
      // Toggle individual style
      if (localSelectedIds.includes(styleId)) {
        // Remove style
        newSelectedIds = localSelectedIds.filter(
          id => id !== styleId && id !== 'all-partner'
        );
      } else {
        // Add style, remove "all-partner" if present
        newSelectedIds = [
          ...localSelectedIds.filter(id => id !== 'all-partner'),
          styleId
        ];
      }
    }

    setLocalSelectedIds(newSelectedIds);
  };

  const handleDone = () => {
    onStylesChange(localSelectedIds);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedStyleIds);
    setIsOpen(false);
  };

  // Empty state - no partner styles exist
  if (!isLoading && fetchedStyles.length === 0) {
    return (
      <div className="w-1/3">
        <label className="text-light-gray mb-1 block text-sm">
          {partnerName} Styles
        </label>
        <div
          className={clsx(
            'text-input-shadow flex h-[2.5rem] w-full items-center justify-between gap-4 overflow-hidden rounded-lg p-3',
            error && 'border-error border-2'
          )}
        >
          <span className="text-light-gray truncate text-sm">
            No custom styles defined
          </span>
          <Link
            to="/styles/new"
            className="text-navy text-sm hover:underline"
            onClick={e => e.stopPropagation()}
          >
            + Create
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-1/3">
      <label className="text-light-gray mb-1 block text-sm">
        {partnerName} Styles
      </label>
      <button
        className={clsx(
          'text-input-shadow flex h-[2.5rem] w-full items-center justify-between gap-4 overflow-hidden rounded-lg p-3',
          error && 'border-error border-2'
        )}
        onClick={() => {
          if (!isLoading && fetchedStyles.length > 0) {
            setIsOpen(true);
          }
        }}
        disabled={isLoading || fetchedStyles.length === 0}
      >
        <span className="truncate">
          {isLoading ? 'Loading...' : getDisplayText()}
        </span>
        <div className="text-aqua">
          <PlusIcon />
        </div>
      </button>

      {isOpen && (
        <div className="relative w-full">
          <div className="text-input-shadow absolute top-2 z-10 flex w-full flex-col rounded-lg bg-white">
            <div className="flex max-h-[40vh] w-full flex-col overflow-y-scroll">
              {styles.map(style => {
                const styleName = styleNames.get(style.style_id) ?? 'Unknown';
                const id = style.style_id ?? '';
                const isSelected =
                  id === 'all-partner'
                    ? isAllSelected
                    : localSelectedIds.includes(id);

                return (
                  <DropdownOption
                    key={id}
                    selected={isSelected}
                    onClick={() => handleToggleStyle(id)}
                  >
                    {styleName}
                  </DropdownOption>
                );
              })}
            </div>

            <div className="flex justify-around p-3">
              <ContainedButton
                className="w-[45%]"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </ContainedButton>
              <ContainedButton className="w-[45%]" onClick={handleDone}>
                Done
              </ContainedButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
